import { useState, useMemo } from "react";
import { Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { ISLANDS, ISLAND_BY_SLUG } from "../../lib/constants";
import { api } from "../../lib/api";

const REASON_LABELS = {
  "invalid_phone:unparseable": "Phone unparseable",
  "invalid_phone:not_possible": "Phone format invalid",
  "invalid_phone:invalid_number": "Phone not a real number",
  "invalid_phone:not_mobile_or_fixed_line": "Not mobile / fixed-line",
  "invalid_phone:empty": "Empty phone",
  "twilio_21610": "Recipient replied STOP",
  "twilio_21614": "Not a valid mobile number (Twilio)",
  "twilio_63007": "WhatsApp channel not enabled",
  "twilio_63013": "Message blocked by Meta policy",
  "twilio_63016": "Outside 24h template window",
};

const humanReason = (reason) => {
  if (!reason) return "";
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  if (reason.startsWith("duplicate_of_")) return "Duplicate of another sub";
  if (reason.startsWith("twilio_")) return `Twilio error ${reason.slice(7)}`;
  return reason;
};

export const SubscribersTab = ({ subscribers, onDelete, onRefresh }) => {
  const [filter, setFilter] = useState("active");
  const [bulkBusy, setBulkBusy] = useState(false);

  const { active, inactive, filtered } = useMemo(() => {
    const a = subscribers.filter((s) => s.active !== false);
    const i = subscribers.filter((s) => s.active === false);
    const f = filter === "active" ? a : filter === "inactive" ? i : subscribers;
    return { active: a, inactive: i, filtered: f };
  }, [subscribers, filter]);

  // Per-island subscriber counts derived from the currently-filtered set so
  // the breakdown moves in lock-step with the Active/Inactive/All toggle.
  //
  // Counting rules (per Calvin's spec, 2026-02):
  //   - Each specific island slug increments only its own counter.
  //   - A subscriber tagged for multiple SPECIFIC islands counts once toward
  //     each of those islands (not distributed).
  //   - The "all" (global) slug is NOT split across per-island counts —
  //     it gets its own separate "All Islands" card. This matters because
  //     the number is quoted to sponsors as real per-island reach.
  //   - `s.islands` defaults to `["all"]` when missing (mirrors row rendering).
  const islandCounts = useMemo(() => {
    const counts = Object.fromEntries(ISLANDS.map((i) => [i.slug, 0]));
    for (const s of filtered) {
      const tags = s.islands && s.islands.length ? s.islands : ["all"];
      for (const slug of tags) {
        if (slug in counts) counts[slug] += 1;
      }
    }
    return counts;
  }, [filtered]);

  // Client-side CSV export of the per-island counter currently on
  // screen. Reuses `islandCounts` and the active filter (`filter`)
  // 1:1 with the pill row — no separate counting logic, so the
  // export can never drift from what admins see. Sponsor decks
  // rely on these numbers, so filename includes the local date to
  // prevent stale reuse of old exports.
  const exportCsv = () => {
    // Basic CSV escape — quote every value and double any embedded
    // quotes. Enough for our fixed columns (Island, Count); no need
    // for a library.
    const csvCell = (v) => `"${String(v).replace(/"/g, '""')}"`;
    // Header row + `Filter` metadata row so admins pasting into a
    // deck can see at a glance whether the numbers reflect Active,
    // Inactive, or All. Then one row per island in the exact same
    // order as the pill row.
    const filterLabels = { active: "Active", inactive: "Inactive", all: "All" };
    const rows = [
      ["Island", "Subscriber Count"],
      ...ISLANDS.map((i) => [i.name, islandCounts[i.slug]]),
      // Blank separator + a small metadata footer — keeps the CSV
      // sponsor-ready while still self-describing.
      [],
      [`Filter: ${filterLabels[filter] || filter}`],
      [`Exported: ${new Date().toISOString()}`],
    ];
    const csv = rows
      .map((row) => row.map(csvCell).join(","))
      .join("\n");
    // Include local date in filename so old exports stay identifiable
    // in a downloads folder (YYYY-MM-DD).
    const isoDate = new Date().toISOString().slice(0, 10);
    const filename = `islandsdaily-subscribers-${isoDate}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bulkDeleteInactive = async () => {
    if (inactive.length === 0) {
      toast.info("No inactive subscribers to delete");
      return;
    }
    if (!window.confirm(
      `Permanently delete ${inactive.length} inactive subscriber(s)? ` +
      `This cannot be undone. Only rows with active=false are affected — ` +
      `${active.length} active subscriber(s) will not be touched.`
    )) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/subscribers/delete-inactive");
      toast.success(`Deleted ${r.data.deleted} inactive subscriber(s)`);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const FilterBtn = ({ value, label, count }) => (
    <button
      onClick={() => setFilter(value)}
      data-testid={`subscribers-filter-${value}`}
      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
        filter === value
          ? "bg-[#0AADA0] text-white"
          : "bg-white text-[#6B7280] hover:text-[#1A1A1A]"
      }`}
    >
      {label}{" "}
      <span className={`ml-1 ${filter === value ? "opacity-80" : "opacity-60"}`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="space-y-4" data-testid="admin-subscribers">
      {/* Temporary internal reminder — the WhatsApp digest is not
          actually sending yet (Meta / Twilio business verification is
          pending, ETA early October 2026). Every subscriber below is
          currently on a wait-list. Hidden automatically once
          REACT_APP_DIGEST_LIVE=true. */}
      {process.env.REACT_APP_DIGEST_LIVE !== "true" && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900"
          role="status"
          data-testid="admin-subscribers-launch-notice"
        >
          <span aria-hidden>⏳</span>
          <span>
            <strong>Waitlist mode.</strong>{" "}
            The daily WhatsApp digest is not sending yet — Meta / Twilio
            business verification is pending (ETA early October 2026).
            Subscribers below are being collected for launch.
          </span>
        </div>
      )}
      {/* Per-island subscriber breakdown. Updates live with the
          Active/Inactive/All filter selection. "All Islands" is a
          separate card — global subscribers are NOT distributed across
          per-island totals (see counting-rules note above). */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="subscribers-island-breakdown"
      >
        <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">
          By island:
        </span>
        {ISLANDS.map((i) => (
          <span
            key={i.slug}
            data-testid={`subscribers-island-count-${i.slug}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-white border border-[#E5E5E5] text-[#1A1A1A]"
            title={i.name}
          >
            <span aria-hidden>{i.flag}</span>
            <span>{i.name}</span>
            <span className="ml-0.5 font-medium text-[#0AADA0]">
              {islandCounts[i.slug]}
            </span>
          </span>
        ))}
        {/* Client-side CSV of the on-screen breakdown. Reuses the
            same `islandCounts` and filter selection so the export can
            never drift from what admins see. Filename carries today's
            date so old exports don't get reused in future decks. */}
        <button
          type="button"
          onClick={exportCsv}
          data-testid="subscribers-export-csv"
          title="Download the on-screen per-island counts as CSV (respects current Active/Inactive/All filter)"
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:border-[#0AADA0] hover:text-[#0AADA0] transition-colors"
        >
          <Download size={12} />
          Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">
          Show:
        </span>
        <FilterBtn value="active" label="Active" count={active.length} />
        <FilterBtn value="inactive" label="Inactive" count={inactive.length} />
        <FilterBtn value="all" label="All" count={subscribers.length} />
        {inactive.length > 0 && (
          <div className="ml-auto">
            <button
              onClick={bulkDeleteInactive}
              disabled={bulkBusy}
              data-testid="subscribers-bulk-delete-inactive-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10 disabled:opacity-50 transition-colors"
            >
              <Trash2 size={12} />
              {bulkBusy
                ? "Deleting…"
                : `Delete all ${inactive.length} inactive`}
            </button>
          </div>
        )}
      </div>

      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#FAFAFA]">
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Islands</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[#6B7280]">
                  No subscribers in this view.
                </td>
              </tr>
            ) : (
              filtered.map((s) => {
                const isActive = s.active !== false;
                return (
                  <tr key={s.id} className="border-t border-[#E5E5E5]">
                    <td className="px-4 py-3 align-top">
                      {isActive ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#DCFCE7] text-[#065F46]"
                          data-testid={`sub-status-active-${s.id}`}
                        >
                          Active
                        </span>
                      ) : (
                        <div
                          className="flex flex-col gap-1"
                          data-testid={`sub-status-inactive-${s.id}`}
                        >
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FEE2E2] text-[#7F1D1D] w-fit">
                            Inactive
                          </span>
                          {s.deactivation_reason && (
                            <span
                              className="text-[10px] text-[#7F1D1D]/80 leading-tight"
                              title={s.deactivation_reason}
                              data-testid={`sub-reason-${s.id}`}
                            >
                              {humanReason(s.deactivation_reason)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A] align-top font-mono text-xs">
                      {s.phone}
                    </td>
                    <td className="px-4 py-3 align-top">{s.name || "—"}</td>
                    <td className="px-4 py-3 text-xs align-top">
                      {(s.islands || ["all"]).map((slug) => {
                        const m = ISLAND_BY_SLUG[slug];
                        return m ? (
                          <span key={slug} title={m.name} className="mr-1">
                            {m.flag}
                          </span>
                        ) : null;
                      })}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] align-top">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <button
                        onClick={() => onDelete(s.id)}
                        className="p-2 rounded-lg text-[#6B7280] hover:bg-[#E63946]/10 hover:text-[#E63946]"
                        data-testid={`admin-del-sub-${s.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
